import type { CatalogImageCase, MarketplaceCode, ProductReviewDetail, ProductReviewKind, ProductReviewRow, ProductReviewWorkflow, ReviewImage } from "./types";
import { reviewImageSourceKey } from "./reviewImageSources";

export function reviewProductTitle(sellerSku: string, ...candidates: Array<string | null | undefined>): string {
  return candidates.map(title => title?.trim()).find(title => title && title !== sellerSku.trim()) || sellerSku;
}

export function reviewTitleMatchesAsin(reference: { asin?: string | null } | undefined, candidate: { asin?: string | null }): boolean {
  const expected = reference?.asin?.trim().toUpperCase(), actual = candidate.asin?.trim().toUpperCase();
  return !expected || !actual || expected === actual;
}

export function reviewQueueTitle(row: ProductReviewRow): string {
  const countries = (row.marketplaces ?? []).filter(country => reviewTitleMatchesAsin(row, country));
  return reviewProductTitle(row.sellerSku, row.germanTitle, ...countries.map(country => country.germanTitle),
    ...countries.filter(country => country.marketplaceCode === "DE").map(country => country.title), row.title,
    ...countries.map(country => country.title));
}

export function reviewHasStock(item: { quantity: number | null; stockKnown?: boolean }): boolean {
  return item.stockKnown !== false && item.quantity !== null && item.quantity > 0;
}

export function catalogImageCases(detail: ProductReviewDetail): CatalogImageCase[] {
  return (detail.images.catalogCases ?? []).filter(imageCase => imageCase.sellerSku === detail.sellerSku
    && imageCase.marketplaceCode === detail.marketplaceCode
    && Boolean(detail.asin && imageCase.asin && detail.asin.toUpperCase() === imageCase.asin.toUpperCase()));
}

export function hasOpenCatalogImageCases(detail: ProductReviewDetail): boolean {
  return catalogImageCases(detail).some(imageCase => !["resolved", "withdrawn"].includes(imageCase.status));
}

export function reviewArchived(detail: ProductReviewDetail, kind: ProductReviewKind): boolean {
  if (kind === "images" && hasOpenCatalogImageCases(detail)) return false;
  const state = detail[kind];
  return Boolean(state.reviewedAt) && state.due !== true && ["reviewed", "completed"].includes(state.status)
    && (kind !== "images" || !detail.images.nextDueAt || new Date(detail.images.nextDueAt).getTime() > Date.now());
}

export function reviewDue(detail: ProductReviewDetail, kind: ProductReviewKind): boolean {
  return reviewHasStock(detail) && (detail[kind].due ?? !reviewArchived(detail, kind));
}

export function reviewEvidenceKey(detail: ProductReviewDetail, kind: ProductReviewKind): string {
  return kind === "images" ? JSON.stringify({ token: detail.images.reviewToken, current: detail.images.current, catalog: detail.images.catalog, draft: detail.images.draft, roles: detail.images.supportedRoles, loadStatus: detail.images.loadStatus, blocked: detail.images.reviewBlockedReason, cases: catalogImageCases(detail).map(imageCase => [imageCase.id, imageCase.revision, imageCase.status, imageCase.checkAfter]) })
    : JSON.stringify({ token: detail.safety.reviewToken, fields: detail.safety.fields, supported: detail.safety.supported });
}

export function imageReviewAvailable(detail: ProductReviewDetail): boolean {
  return Boolean(detail.images.reviewToken) && (!detail.images.loadStatus || detail.images.loadStatus === "ready")
    && !detail.images.reviewBlockedReason && Boolean(detail.images.current.length || detail.images.catalog.length);
}

export function canRestartImageReview(detail: ProductReviewDetail): boolean {
  return reviewDue(detail, "images") && imageReviewAvailable(detail)
    && !hasOpenCatalogImageCases(detail)
    && ["verification_failed", "failed"].includes(detail.images.status)
    && Boolean(detail.images.submissionAttemptedAt)
    && !productReviewHasErrors({ ...detail, images: { ...detail.images, processingErrors: [] } }, "images");
}

export function reviewSelectable(detail: ProductReviewDetail, kind: ProductReviewKind): boolean {
  return reviewDue(detail, kind) && (kind !== "images" || imageReviewAvailable(detail));
}

export function imageReviewUnavailableMessage(detail: ProductReviewDetail): string | null {
  if (detail.images.reviewBlockedReason) return detail.images.reviewBlockedReason;
  if (detail.images.loadMessage && detail.images.loadStatus !== "ready") return detail.images.loadMessage;
  if (!detail.images.current.length && !detail.images.catalog.length) return "Keine aktuellen Produktbilder verfügbar. Die Bilderprüfung bleibt offen.";
  return !imageReviewAvailable(detail) ? "Die Bilddaten sind unvollständig. Die Bilderprüfung bleibt offen." : null;
}

export function imageInspectionBlockedReason(detail: ProductReviewDetail): string | null {
  if (!reviewHasStock(detail)) return detail.quantity === null || detail.stockKnown === false
    ? "Der Bestand ist nicht bestätigt. Bitte den Amazon-Bestand erneut laden."
    : "Ohne Bestand ist keine Bilderprüfung fällig.";
  if (hasOpenCatalogImageCases(detail)) return "Ein Katalogbild-Fall ist offen. Prüfe den Fall oder nimm die Beanstandung ausdrücklich zurück.";
  if (!reviewDue(detail, "images")) return "Diese Bilderprüfung ist aktuell nicht fällig.";
  return imageReviewUnavailableMessage(detail);
}

export function groupedReviewRows(rows: ProductReviewRow[]): ProductReviewRow[] {
  const groups = new Map<string, ProductReviewRow>();
  for (const row of rows) {
    const previous = groups.get(row.sellerSku);
    const countries = [...(previous?.marketplaces ?? []), ...(row.marketplaces ?? [row])];
    const unique = [...new Map(countries.map(country => [country.marketplaceCode, country])).values()];
    groups.set(row.sellerSku, { ...(previous ?? row), marketplaces: unique });
  }
  return [...groups.values()];
}

export function commonImageRoles(details: ProductReviewDetail[]): string[] {
  return details.length ? details[0].images.supportedRoles.filter(role => details.every(detail => detail.images.supportedRoles.includes(role))) : [];
}

export function bundledLiveImages(details: ProductReviewDetail[]): Array<ReviewImage & { key: string; countries: Array<{ marketplaceCode: MarketplaceCode; role: string; source: string }> }> {
  const images = new Map<string, ReviewImage & { key: string; countries: Array<{ marketplaceCode: MarketplaceCode; role: string; source: string }> }>();
  for (const detail of details) {
    for (const image of liveReviewImages(detail)) {
      const key = image.url ? `image:${reviewImageSourceKey(image)}` : `image:${detail.marketplaceCode}:${image.key}`;
      const existing = images.get(key) ?? { ...image, key, countries: [] };
      existing.countries.push({ marketplaceCode: detail.marketplaceCode, role: image.role, source: image.source });
      images.set(key, existing);
    }
  }
  return [...images.values()];
}

export function awaitingProductReview(status: string): boolean {
  return ["submitting", "submitted", "pending", "pending_verification", "awaiting_verification", "processing", "queued"].includes(status);
}

export function orderedImageRoles(supportedRoles: string[]): string[] {
  return supportedRoles.filter(role => role === "MAIN" || /^PT\d{2}$/.test(role)).sort((a, b) => a === "MAIN" ? -1 : b === "MAIN" ? 1 : a.localeCompare(b));
}

export function assignImageRoles(images: ReviewImage[], supportedRoles: string[]): ReviewImage[] {
  const roles = orderedImageRoles(supportedRoles);
  let index = 0;
  return images.map(image => image.role === "SWCH" ? image : { ...image, role: roles[index++] ?? image.role });
}

export function moveImage(images: ReviewImage[], from: number, to: number, supportedRoles: string[]): ReviewImage[] {
  if (images[from]?.role === "SWCH" || images[to]?.role === "SWCH" || from < 0 || to < 0 || to >= images.length) return images;
  const next = [...images];
  const [image] = next.splice(from, 1);
  next.splice(to, 0, image);
  return assignImageRoles(next, supportedRoles);
}

export function imagePayload(images: ReviewImage[]): ReviewImage[] {
  return images.map(({ role, url, assetId }) => ({ role, ...(assetId ? { assetId } : { url }) }));
}

export function liveReviewImages(detail: ProductReviewDetail): Array<ReviewImage & { key: string; source: string }> {
  const seen = new Set<string>();
  const result: Array<ReviewImage & { key: string; source: string }> = [];
  for (const [source, images] of [["Eigener Amazon-Beitrag", detail.images.current], ["Amazon-Katalog", detail.images.catalog]] as const) {
    for (const image of images) {
      const key = `${image.role}:${reviewImageSourceKey(image)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ ...image, key, source });
    }
  }
  return result;
}

export function safetyAttributes(detail: ProductReviewDetail): Record<string, unknown> {
  return Object.fromEntries(detail.safety.fields.map(field => [field.name, field.currentValue]));
}

export function changedSafetyAttributes(detail: ProductReviewDetail, attributes: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(detail.safety.fields
    .filter(field => field.editable && JSON.stringify(attributes[field.name]) !== JSON.stringify(field.currentValue))
    .map(field => [field.name, attributes[field.name]]));
}

export function reviewStatus(status: string): string {
  const labels: Record<string, string> = {
    due: "Prüfung fällig", pending: "Amazon verarbeitet", submitting: "Übermittlung an Amazon läuft", submitted: "An Amazon gesendet",
    correction_review_due: "Bildkorrektur erneut prüfen", correction_upload_failed: "Bildupload fehlgeschlagen", correction_processing: "Upload wird geprüft", correction_awaiting_upload: "Bilder hochladen", correction_ready: "Bildbeanstandung bearbeiten", correction_waiting: "Wartezeit nach Upload", queued: "Übermittlung vorbereitet", pending_verification: "Live-Abgleich ausstehend", awaiting_verification: "Live-Abgleich ausstehend", verified: "Amazon-Beitrag bestätigt", verification_failed: "Live-Abgleich nicht bestätigt",
    reviewed: "Manuell geprüft", completed: "Manuell geprüft", not_due: "Prüfung aktuell",
    unsupported: "Amazon-Regeln nicht verfügbar", draft: "Entwurf gespeichert", failed: "Übermittlung oder Verarbeitung fehlgeschlagen",
    case_waiting: "Fall eröffnet · pausiert", case_check: "Fall prüfen", case_check_failed: "Fall prüfen · Bildstatus nicht bestätigt",
  };
  return labels[status] ?? status.replaceAll("_", " ");
}

export function uploadedImageStatus(image: ReviewImage, savedImages: ReviewImage[], state: ProductReviewWorkflow & { status: string }): string {
  const status = state.status;
  const saved = savedImages.some(item => item.role === image.role && item.assetId === image.assetId);
  if (!saved) return "Hochgeladen · noch nicht übermittelt";
  if (["verified", "reviewed", "completed"].includes(status)) return "Amazon-Beitrag bestätigt; Katalogveröffentlichung separat";
  if (status === "queued") return "Hochgeladen · wartet auf Übermittlung an Amazon";
  if (status === "submitting") return "Übermittlung an Amazon läuft · Annahme noch offen";
  if (awaitingProductReview(status)) return "An Amazon übermittelt · Übernahme wird geprüft; Katalogveröffentlichung separat";
  if (status === "verification_failed") return "Amazon-Übernahme nicht bestätigt · Amazon-Hinweise und Abgleich prüfen";
  if (status === "failed") return state.submittedAt ? "Von Amazon angenommen · Verarbeitung fehlgeschlagen; Meldungen prüfen" : "Übermittlung fehlgeschlagen · Amazon-Hinweise prüfen";
  return "Hochgeladen · noch nicht übermittelt";
}

export function productReviewHasErrors(detail: ProductReviewDetail, kind: ProductReviewKind): boolean {
  const state = detail[kind];
  if (state.processingErrors?.length) return true;
  return (state.amazonIssues ?? detail.amazonIssues ?? []).some(issue => {
    if (issue.severity?.toUpperCase() !== "ERROR") return false;
    if (["5885", "5886", "5887", "100334"].includes(String(issue.code)) || !issue.attributeNames?.length) return true;
    return issue.attributeNames.some(reference => {
      const attribute = reference.trim().replace(/^(\$\.attributes\.|\/attributes\/|attributes\.)/, "").match(/^([A-Za-z][A-Za-z0-9_]*)(?:(?:#\d+)|(?:\[\d+\])|(?:\.[A-Za-z0-9_]+)|(?:\/[A-Za-z0-9_]+))*$/)?.[1];
      return kind === "images"
        ? Boolean(attribute && /^(main_product_image_locator|other_product_image_locator_\d+|swatch_product_image_locator)$/.test(attribute))
        : detail.safety.fields.some(field => field.name === attribute);
    });
  });
}

export function rebaseSafetyDraft(previous: ProductReviewDetail, fresh: ProductReviewDetail, attributes: Record<string, unknown>, existingConflicts: string[]) {
  const changes = changedSafetyAttributes(previous, attributes);
  const current = safetyAttributes(fresh);
  const conflicts = [...new Set([...existingConflicts, ...Object.keys(changes).filter(name =>
    JSON.stringify(safetyAttributes(previous)[name]) !== JSON.stringify(current[name])
  )])].filter(name => JSON.stringify(attributes[name]) !== JSON.stringify(current[name]));
  return { attributes: { ...current, ...changes }, conflicts };
}

